import { Component, computed, inject, OnInit, signal, Type, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { MDHeaderComponent } from '@melodic-ui/components';
import { LoaderComponent } from '../../loader/loader.component';
import { CalendarService } from '@lb/api/mylb/services';
import { ICalendarTokenStatusResponse } from '@lb/api/mylb/interfaces/icalendar-token-status-response.interface';
import { MDDialogService } from '@melodic-ui/components';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { tap } from 'rxjs';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';

@Component({
	selector: 'lb-calendar-advanced-sync',
	standalone: true,
	imports: [CommonModule, MDHeaderComponent, LoaderComponent, ClipboardModule, DatePipe],
	templateUrl: './calendar-advanced-sync.component.html',
	styleUrl: './calendar-advanced-sync.component.scss'
})
export class CalendarAdvancedSyncComponent implements OnInit {
	private _calendarService = inject(CalendarService);
	private _dialogService = inject(MDDialogService);
	private _clipboard = inject(Clipboard);
	private _appConfig: IAppSettings = inject(APP_CONFIG);

	public calendarTokenStatus: WritableSignal<ICalendarTokenStatusResponse | null> = signal(null);
	public calendarLoading: WritableSignal<boolean> = signal(false);
	public linkCopied: WritableSignal<boolean> = signal(false);
	public enableCalendarSync: boolean = this._appConfig.features?.enableCalendarSync ?? false;

	public isSynced = computed(() => this.calendarTokenStatus()?.isConfigured ?? false);

	public isExpiringSoon = computed(() => {
		const daysUntilExpiration = this.calendarTokenStatus()?.daysUntilExpiration;
		return daysUntilExpiration !== null && daysUntilExpiration !== undefined && daysUntilExpiration < 30;
	});

	ngOnInit(): void {
		if (this.enableCalendarSync) {
			this.loadCalendarTokenStatus();
		}
	}

	loadCalendarTokenStatus(): void {
		this.calendarLoading.set(true);
		this._calendarService.getTokenStatus().subscribe({
			next: (status) => {
				this.calendarTokenStatus.set(status);
				this.calendarLoading.set(false);
			},
			error: () => {
				this.calendarTokenStatus.set(null);
				this.calendarLoading.set(false);
			}
		});
	}

	generateCalendarToken(): void {
		this.calendarLoading.set(true);
		this._calendarService.generateToken().subscribe({
			next: (status) => {
				this.calendarTokenStatus.set(status);
				this.calendarLoading.set(false);
			},
			error: (_error: unknown) => {
				this.calendarLoading.set(false);
			}
		});
	}

	renewCalendarToken(): void {
		this.calendarLoading.set(true);
		this._calendarService.renewToken().subscribe({
			next: (status) => {
				this.calendarTokenStatus.set(status);
				this.calendarLoading.set(false);
			},
			error: () => {
				this.calendarLoading.set(false);
			}
		});
	}

	revokeCalendarToken(): void {
		this._dialogService.open(ConfirmationDialogComponent as Type<any>, {
			title: 'Disable Calendar Sync?',
			body: 'Are you sure you want to disable calendar sync? Your calendar link will stop working immediately.',
			successText: 'Disable',
			icon: 'untitled-ui/alerts/alert-triangle',
			action: () => {
				return this._calendarService.revokeToken().pipe(
					tap(() => {
						this.calendarTokenStatus.set(null);
					})
				);
			}
		});
	}

	addToGoogleCalendar(url: string): void {
		// Ensure protocol is https (Google Calendar requires https for server-side fetching)
		const httpsUrl = url.replace(/^webcal:\/\//, 'https://');

		// Add cache buster to force Google to re-fetch.
		// Google eagerly caches failures (like 401s), so a new URL is required to clear the error.
		const uniqueUrl = httpsUrl + (httpsUrl.includes('?') ? '&' : '?') + 'nocache=' + Date.now();

		// Use the legacy 'render' endpoint which reliably supports 'cid'
		const googleUrl = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(uniqueUrl)}`;
		window.open(googleUrl, '_blank');
	}

	addToOutlookCalendar(url: string): void {
		// Outlook Personal (Live) works best with https:// for the url parameter
		const outlookUrl = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent('LanguageBird Calendar')}`;
		window.open(outlookUrl, '_blank');
	}

	addToOffice365Calendar(url: string): void {
		// Office 365 (Business/School) works best with https:// for the url parameter
		const officeUrl = `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent('LanguageBird Calendar')}`;
		window.open(officeUrl, '_blank');
	}

	copyToClipboard(text: string): void {
		this._clipboard.copy(text);
		this.linkCopied.set(true);
		setTimeout(() => {
			this.linkCopied.set(false);
		}, 2000);
	}
}
